import type { AgentKeyCreated, AgentMember, AgentRole, WorkspaceCollaboratorRole } from "../services/naraDoor";

/** An AI agent is never admin or owner (it could add members or keys) nor a field agent. */
export const AGENT_ROLES: AgentRole[] = ["viewer", "editor"];

/** nara-core marks an AI agent member with `kind`, on the collaborator row or on its user. */
export const isAgent = (c?: { kind?: string; user?: { kind?: string } | null }): boolean =>
  (c?.kind ?? c?.user?.kind) === "agent";

/** The roles a role picker offers: the agent's two, else the caller's list. */
export const roleChoices = (agent: boolean, roles: WorkspaceCollaboratorRole[]): WorkspaceCollaboratorRole[] =>
  agent ? roles.filter((r): r is AgentRole => AGENT_ROLES.includes(r as AgentRole)) : roles;

/** An agent going from Viewer to Editor can change content, so the admin consents again (D5). */
export const needsEditorConsent = (agent: boolean, from: WorkspaceCollaboratorRole | null, to: WorkspaceCollaboratorRole): boolean =>
  agent && from === "viewer" && to === "editor";

export const AGENT_ENDPOINT = "https://door.naramachine.ai/agent/mcp";

export interface AddAgentState {
  step: "form" | "consent" | "key" | "keyFailed";
  name: string;
  role: AgentRole;
  /** The admin ticked the consent box (D5). */
  accepted: boolean;
  /** The raw key: held only while the key step is open. */
  key: string | null;
  error: "nameRequired" | "createFailed" | null;
}

export type AddAgentAction =
  | { type: "setName"; name: string }
  | { type: "setRole"; role: AgentRole }
  | { type: "continue" }
  | { type: "back" }
  | { type: "setAccepted"; accepted: boolean }
  | { type: "created"; key: string | null }
  | { type: "failed" }
  | { type: "reset" };

export const initialAddAgent: AddAgentState = { step: "form", name: "", role: "viewer", accepted: false, key: null, error: null };

export function addAgentReducer(state: AddAgentState, action: AddAgentAction): AddAgentState {
  switch (action.type) {
    case "setName":
      return { ...state, name: action.name, error: null };
    case "setRole":
      return { ...state, role: action.role };
    case "continue":
      return state.name.trim() ? { ...state, step: "consent", accepted: false, error: null } : { ...state, error: "nameRequired" };
    case "back":
      return { ...state, step: "form", accepted: false, error: null };
    case "setAccepted":
      return { ...state, accepted: action.accepted };
    case "created":
      return { ...state, step: action.key ? "key" : "keyFailed", key: action.key, error: null };
    case "failed":
      return { ...state, error: "createFailed" };
    case "reset":
      return initialAddAgent;
  }
}

/** The agent is created only from the consent step, with the box ticked. */
export const canConfirmAgent = (state: AddAgentState): boolean => state.step === "consent" && state.accepted;

const errorCode = (err: unknown) => (err as { code?: string }).code;

/**
 * Creates the agent, then its first key. Resolves to the action for the dialog: `failed` when the
 * agent could not be created, `created` with a null key when only the key failed. Logs ids and codes
 * only: never the name nor the key.
 */
export async function runAddAgent(
  createMember: (body: { display_name: string; role: AgentRole }) => Promise<AgentMember>,
  createKey: (userId: string) => Promise<AgentKeyCreated>,
  workspaceId: string,
  name: string,
  role: AgentRole
): Promise<AddAgentAction> {
  let userId: string;
  try {
    userId = (await createMember({ display_name: name.trim(), role })).user_id;
  } catch (err) {
    console.error("createAgentMember failed", { workspaceId, code: errorCode(err) });
    return { type: "failed" };
  }
  try {
    return { type: "created", key: (await createKey(userId)).key };
  } catch (err) {
    console.error("createAgentKey failed", { workspaceId, userId, code: errorCode(err) });
    return { type: "created", key: null };
  }
}
